import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha, Alert, Box, Button, Checkbox, Chip, InputAdornment, Pagination, Stack, TextField, Typography
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import { CATEGORIES, DORK_LIBRARY, SEARCH_ENGINES } from './dorkData';

const GREEN = '#22c55e';
const MONO = 'var(--fuzzscan-ui-font-mono)';
const PAGE_SIZE = 20;
const URL_LIMIT = 1800; // search engines start rejecting very long queries around here
const CATS = CATEGORIES.filter((c) => c.id !== 'all');
const catOf = (id) => CATS.find((c) => c.id === id) || CATS[0];

const hostOf = (raw) => {
  const v = String(raw || '').trim();
  try { return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `//${v}`, 'http://x').hostname || v; } catch { return v; }
};
const copy = (text) => navigator.clipboard.writeText(text).catch(() => {});
const openSearch = (engineId, query) => {
  const engine = SEARCH_ENGINES.find((e) => e.id === engineId) || SEARCH_ENGINES[0];
  window.open(`${engine.url}${encodeURIComponent(query)}`, '_blank', 'noopener');
};

export default function DorkEnginePage() {
  const { targets, currentTargetId } = useFuzzScanTarget();
  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)), [targets, currentTargetId]);
  const [domain, setDomain] = useState('');
  const [engine, setEngine] = useState('google');
  const [mode, setMode] = useState('OR');
  const [cats, setCats] = useState(['subdomains', 'admin', 'api', 'credentials', 'files', 'login']);
  const [picked, setPicked] = useState([]); // dork keys the user ticked
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [flash, setFlash] = useState('');

  // Start from the current target; the user can still type any domain.
  useEffect(() => {
    const t = userTarget?.target;
    if (t && t.type !== 'mobile' && t.value) setDomain(hostOf(t.value));
  }, [userTarget]);

  const host = domain.trim();
  const all = useMemo(() => DORK_LIBRARY.map((d, i) => ({ ...d, key: `${d.cat}:${i}`, q: d.q.replaceAll('{domain}', host) })), [host]);
  const countOf = (id) => DORK_LIBRARY.filter((d) => d.cat === id).length;

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((d) => cats.includes(d.cat) && (!s || d.label.toLowerCase().includes(s) || d.q.toLowerCase().includes(s)));
  }, [all, cats, search]);
  const paged = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Ticked queries drive the search; with none ticked it uses everything that is listed.
  const chosen = picked.length ? all.filter((d) => picked.includes(d.key)) : visible;
  const combined = chosen.map((d) => `(${d.q})`).join(` ${mode} `);
  const tooLong = combined.length > URL_LIMIT;
  const engineLabel = SEARCH_ENGINES.find((e) => e.id === engine)?.label;

  const say = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 1600); };
  const toggleCat = (id) => { setCats((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])); setPage(1); };
  const togglePick = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const pageAllPicked = paged.length > 0 && paged.every((d) => picked.includes(d.key));
  const togglePage = () => setPicked((p) => (pageAllPicked ? p.filter((k) => !paged.some((d) => d.key === k)) : [...new Set([...p, ...paged.map((d) => d.key)])]));

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Dork Engine</Typography>
        <Typography variant="body2" color="text.secondary">
          Ready-made search queries that reveal exposed pages, files and leaks for a domain you are allowed to test
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2.5} alignItems="flex-start">
        {/* builder */}
        <Box sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0, border: 1, borderColor: 'divider', borderRadius: 4, p: 2.5, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <TextField fullWidth size="small" label="Domain" placeholder="example.com" value={domain} onChange={(e) => { setDomain(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Box component="span" sx={{ fontFamily: MONO, color: 'text.secondary' }}>@</Box></InputAdornment> }} />
            {userTarget && <Typography variant="caption" color="text.secondary">Taken from your current target. You can type any domain.</Typography>}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Search engine</Typography>
            <Stack direction="row" gap={0.7} flexWrap="wrap" sx={{ mt: 0.7 }}>
              {SEARCH_ENGINES.map((e) => (
                <Chip key={e.id} clickable label={e.label} onClick={() => setEngine(e.id)}
                  sx={{ fontWeight: 600, border: 1, borderColor: engine === e.id ? alpha(GREEN, 0.5) : 'divider', bgcolor: engine === e.id ? alpha(GREEN, 0.16) : 'transparent', color: engine === e.id ? GREEN : 'text.secondary' }} />
              ))}
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">What to look for</Typography>
              <Box>
                <Button size="small" onClick={() => { setCats(CATS.map((c) => c.id)); setPage(1); }} sx={{ color: GREEN, fontWeight: 600, minWidth: 0 }}>All</Button>
                <Button size="small" onClick={() => { setCats([]); setPage(1); }} sx={{ color: 'text.secondary', fontWeight: 600, minWidth: 0 }}>None</Button>
              </Box>
            </Stack>
            {CATS.map((c) => (
              <Stack key={c.id} direction="row" alignItems="center" onClick={() => toggleCat(c.id)}
                sx={{ py: 0.2, pr: 1, borderRadius: 2, cursor: 'pointer', bgcolor: cats.includes(c.id) ? 'action.hover' : 'transparent' }}>
                <Checkbox size="small" checked={cats.includes(c.id)} tabIndex={-1} inputProps={{ 'aria-label': c.label }} sx={{ color: 'text.disabled', '&.Mui-checked': { color: GREEN } }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, mr: 1.2 }} />
                <Typography sx={{ flex: 1, fontSize: 14 }}>{c.label}</Typography>
                <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary' }}>{countOf(c.id)}</Typography>
              </Stack>
            ))}
          </Box>

          <Stack direction="row" gap={0.8}>
            {['OR', 'AND'].map((m) => (
              <Button key={m} fullWidth variant={mode === m ? 'contained' : 'outlined'} onClick={() => setMode(m)}
                sx={{ fontWeight: 600, ...(mode === m ? { bgcolor: 'action.selected', color: 'text.primary', boxShadow: 'none', '&:hover': { bgcolor: 'action.selected' } } : { borderColor: 'divider', color: 'text.secondary' }) }}>
                Combine with {m}
              </Button>
            ))}
          </Stack>
        </Box>

        {/* results */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%', border: 1, borderColor: 'divider', borderRadius: 4, bgcolor: 'background.paper', overflow: 'hidden' }}>
          {!host ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700 }}>Enter a domain to get started</Typography>
              <Typography variant="body2" color="text.secondary">The queries are written for that domain as soon as you type it.</Typography>
            </Box>
          ) : (
            <>
              <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                    {picked.length ? `${picked.length} selected of ${visible.length}` : `${visible.length} queries`} for <Box component="span" sx={{ fontFamily: MONO }}>{host}</Box>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Tick the ones you want, or use them all. Opens in {engineLabel}.</Typography>
                </Box>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {picked.length > 0 && <Button onClick={() => setPicked([])} sx={{ color: 'text.secondary', fontWeight: 600 }}>Clear</Button>}
                  <Button variant="outlined" disabled={!chosen.length} onClick={() => { copy(chosen.map((d) => d.q).join('\n')); say('Copied.'); }}
                    sx={{ borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>{flash || (picked.length ? `Copy ${picked.length}` : 'Copy all')}</Button>
                  <Button variant="contained" disabled={!chosen.length} onClick={() => openSearch(engine, combined)}
                    sx={{ bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>{picked.length ? 'Open selected' : 'Open combined'}</Button>
                </Stack>
              </Stack>

              {picked.length > 0 && (
                <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', bgcolor: (t) => alpha(GREEN, t.palette.mode === 'dark' ? 0.05 : 0.04) }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ color: GREEN, fontWeight: 700, letterSpacing: 1 }}>Your search</Typography>
                    <Typography variant="caption" color={tooLong ? 'warning.main' : 'text.secondary'}>
                      {combined.length} characters{tooLong ? ' · may be too long for some engines, try fewer queries' : ''}
                    </Typography>
                  </Stack>
                  <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: (t) => (t.palette.mode === 'dark' ? '#0a0f14' : '#f1f5f9'), fontFamily: MONO, fontSize: 12.5, lineHeight: 1.7, wordBreak: 'break-all', maxHeight: 130, overflow: 'auto' }}>
                    {combined}
                  </Box>
                  <Stack direction="row" gap={1} sx={{ mt: 1.2 }}>
                    <Button size="small" variant="outlined" onClick={() => { copy(combined); say('Copied.'); }} sx={{ borderColor: 'divider', color: 'text.primary' }}>Copy search</Button>
                  </Stack>
                </Box>
              )}

              <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Checkbox size="small" checked={pageAllPicked} onChange={togglePage} inputProps={{ 'aria-label': 'Tick all queries on this page' }} sx={{ '&.Mui-checked': { color: GREEN } }} />
                <TextField size="small" fullWidth value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search the queries"
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              </Stack>

              {paged.length === 0 && <Alert severity="info" sx={{ m: 2.5 }}>{cats.length ? 'No query matches your search.' : 'Choose at least one category on the left.'}</Alert>}
              {paged.map((d) => {
                const c = catOf(d.cat);
                const on = picked.includes(d.key);
                return (
                  <Stack key={d.key} direction="row" alignItems="center" gap={1.2} sx={{ pl: 2, pr: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider', bgcolor: on ? alpha(GREEN, 0.06) : 'transparent' }}>
                    <Checkbox size="small" checked={on} onChange={() => togglePick(d.key)} inputProps={{ 'aria-label': `Tick ${d.label}` }} sx={{ '&.Mui-checked': { color: GREEN } }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{d.label}</Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary', wordBreak: 'break-all' }}>{d.q}</Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => copy(d.q)} sx={{ borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Copy</Button>
                    <Button size="small" variant="outlined" onClick={() => openSearch(engine, d.q)} sx={{ borderColor: 'divider', color: GREEN, fontWeight: 600 }}>Open</Button>
                  </Stack>
                );
              })}
              {visible.length > PAGE_SIZE && (
                <Stack alignItems="center" sx={{ p: 2 }}>
                  <Pagination count={Math.ceil(visible.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} size="small" />
                </Stack>
              )}
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
